/**
 * Enrollment Modal — 3-step enrollment flow for runners.
 *
 * Step 1: Install — download and install the agent binary
 * Step 2: Enroll — run the enrollment command with generated key
 * Step 3: Connect — verify agent connects to the platform
 *
 * Sprint: 73, Task: S73-B03
 */

import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useI18n } from '@/i18n';
import {
  Button,
  Dialog,
  DialogContent,
  Input,
  Label,
} from '@/components/ui';
import { DiscardChangesDialog } from '@vanguard-atlas/shared';
import { useEnrollRunner, useRunner } from '@/features/mcp';
import type { EnrollmentInitiateResponse } from '@/features/mcp';
import { Copy, Check, Key, Download, ExternalLink, Loader2, Terminal, Wifi } from 'lucide-react';
import { toast } from 'sonner';

interface EnrollmentModalProps {
  onClose: () => void;
}

type FlowStep = 'generate' | 'install' | 'enroll' | 'connect';

const STEP_CONFIG = [
  { key: 'install' as const, number: 1, icon: Download },
  { key: 'enroll' as const, number: 2, icon: Terminal },
  { key: 'connect' as const, number: 3, icon: Wifi },
] as const;

export function EnrollmentModal({ onClose }: EnrollmentModalProps) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const enrollMutation = useEnrollRunner();

  const [name, setName] = useState('');
  const [flowStep, setFlowStep] = useState<FlowStep>('generate');
  const [enrollment, setEnrollment] = useState<EnrollmentInitiateResponse | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [enrollmentCompleted, setEnrollmentCompleted] = useState(false);
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);

  // ADR-114 enmienda 1: dirty = nombre introducido antes de generar el enrollment.
  const isDirty =
    name.trim() !== '' && !enrollment && !enrollmentCompleted;
  const discardLabels = {
    title: t('common.discardChanges.title') ?? 'Discard changes?',
    message:
      t('common.discardChanges.message') ??
      'You have unsaved changes. If you close now, they will be lost.',
    confirm: t('common.discardChanges.discard') ?? 'Discard',
    cancel: t('common.discardChanges.keepEditing') ?? 'Keep editing',
  };
  const handleClose = () => {
    if (isDirty) {
      setConfirmDiscardOpen(true);
      return;
    }
    onClose();
  };

  // Poll for enrollment completion
  const { data: runnerData } = useRunner(enrollment?.agent_id ?? '');

  useEffect(() => {
    if (runnerData && runnerData.status !== 'pending' && !enrollmentCompleted) {
      setEnrollmentCompleted(true);
      setFlowStep('connect');
      toast.success(t('admin.runners.enrollment.completed'));
    }
  }, [runnerData, enrollmentCompleted, t]);

  // Token expiration countdown
  const expiryInfo = useMemo(() => {
    if (!enrollment) return null;
    const diffMs = new Date(enrollment.expires_at).getTime() - Date.now();
    const hours = Math.floor(diffMs / 3600000);
    const minutes = Math.floor((diffMs % 3600000) / 60000);
    const expired = diffMs <= 0;
    return { hours, minutes, expired };
  }, [enrollment]);

  const handleGenerate = async () => {
    if (!name.trim()) return;
    try {
      const result = await enrollMutation.mutateAsync({ name: name.trim() });
      setEnrollment(result);
      setFlowStep('install');
    } catch (err) {
      toast.error((err as Error).message || 'Failed to generate enrollment token');
    }
  };

  const copyToClipboard = async (text: string, field: string) => {
    await navigator.clipboard.writeText(text);
    setCopiedField(field);
    toast.success(t('admin.runners.enrollment.copied'));
    setTimeout(() => setCopiedField(null), 2000);
  };

  const activeStepIndex = flowStep === 'install' ? 0 : flowStep === 'enroll' ? 1 : flowStep === 'connect' ? 2 : -1;

  return (
    <Dialog open onOpenChange={(next) => { if (!next) handleClose(); }}>
      <DialogContent
        className="max-w-lg"
        dirty={isDirty}
        discardTitle={discardLabels.title}
        discardMessage={discardLabels.message}
        discardConfirmLabel={discardLabels.confirm}
        discardCancelLabel={discardLabels.cancel}
      >
        <h2 className="text-lg font-semibold mb-1">
          {t('admin.runners.enrollment.title')}
        </h2>

        {/* Generate step — name input (pre-flow) */}
        {flowStep === 'generate' && (
          <div className="space-y-4 mt-4">
            <p className="text-sm text-muted-foreground">
              {t('admin.runners.enrollment.step1Description')}
            </p>
            <div>
              <Label htmlFor="agent-name">
                {t('admin.runners.enrollment.agentName')}
              </Label>
              <Input
                id="agent-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('admin.runners.enrollment.agentNamePlaceholder')}
                autoFocus
                onKeyDown={(e) => e.key === 'Enter' && handleGenerate()}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={handleClose}>
                {t('common.cancel')}
              </Button>
              <Button
                onClick={handleGenerate}
                disabled={!name.trim() || enrollMutation.isPending}
              >
                {enrollMutation.isPending ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Key className="w-4 h-4 mr-2" />
                )}
                {t('admin.runners.enrollment.generate')}
              </Button>
            </div>
          </div>
        )}

        {/* 3-step flow (after token generated) */}
        {flowStep !== 'generate' && enrollment && (
          <>
            {/* 3-step progress indicator */}
            <div className="flex items-center gap-2 mb-6 mt-4">
              {STEP_CONFIG.map((stepCfg, idx) => {
                const StepIcon = stepCfg.icon;
                const isActive = idx === activeStepIndex;
                const isComplete = idx < activeStepIndex || (idx === 2 && enrollmentCompleted);
                return (
                  <div key={stepCfg.key} className="flex items-center gap-2 flex-1">
                    <button
                      onClick={() => !enrollmentCompleted && setFlowStep(stepCfg.key)}
                      className={`flex items-center gap-1.5 text-xs transition-colors ${
                        isActive ? 'text-primary font-semibold' : isComplete ? 'text-green-600 font-medium' : 'text-muted-foreground'
                      }`}
                    >
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${
                        isComplete ? 'bg-green-100 dark:bg-green-900' : isActive ? 'bg-primary text-primary-foreground' : 'bg-muted'
                      }`}>
                        {isComplete ? <Check className="w-3.5 h-3.5 text-green-600" /> : <StepIcon className={`w-3.5 h-3.5 ${isActive ? 'text-primary-foreground' : ''}`} />}
                      </div>
                      {t(`admin.runners.enrollment.flow.${stepCfg.key}`)}
                    </button>
                    {idx < STEP_CONFIG.length - 1 && (
                      <div className={`flex-1 h-px ${isComplete ? 'bg-green-400' : 'bg-border'}`} />
                    )}
                  </div>
                );
              })}
            </div>

            {/* Token expiry banner */}
            {expiryInfo && !expiryInfo.expired && (
              <div className="text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 rounded-md px-3 py-1.5 mb-4 flex items-center gap-1.5">
                <Key className="w-3.5 h-3.5 shrink-0" />
                Token expires in {expiryInfo.hours > 0 ? `${expiryInfo.hours}h ` : ''}{expiryInfo.minutes}m
              </div>
            )}
            {expiryInfo?.expired && (
              <div className="text-xs text-red-600 bg-red-50 dark:bg-red-950/30 rounded-md px-3 py-1.5 mb-4">
                Token expired — please generate a new enrollment.
              </div>
            )}

            {/* Step 1: Install */}
            {flowStep === 'install' && (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Download and install the Atlas Agent on the target machine.
                </p>

                <div>
                  <Label className="text-xs text-muted-foreground mb-1">Install Command</Label>
                  <div className="relative">
                    <pre className="bg-muted/50 rounded-lg p-3 pr-10 text-xs font-mono overflow-x-auto">
                      {enrollment.install_command}
                    </pre>
                    <button
                      onClick={() => copyToClipboard(enrollment.install_command, 'install')}
                      className="absolute top-2 right-2 text-muted-foreground hover:text-foreground"
                    >
                      {copiedField === 'install' ? (
                        <Check className="w-4 h-4 text-green-500" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>

                <Link
                  to="/integrations/download-agent"
                  className="text-sm text-primary hover:underline flex items-center gap-1"
                  onClick={onClose}
                >
                  <Download className="w-3.5 h-3.5" />
                  Or download binary manually
                  <ExternalLink className="w-3 h-3 ml-0.5" />
                </Link>

                <div className="flex justify-end">
                  <Button onClick={() => setFlowStep('enroll')}>
                    Next: Enroll
                  </Button>
                </div>
              </div>
            )}

            {/* Step 2: Enroll */}
            {flowStep === 'enroll' && (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Run this command on the machine where you installed the agent.
                </p>

                <div>
                  <Label className="text-xs text-muted-foreground mb-1">Enroll Command</Label>
                  <div className="relative">
                    <pre className="bg-muted/50 rounded-lg p-3 pr-10 text-xs font-mono overflow-x-auto">
                      {enrollment.enroll_command}
                    </pre>
                    <button
                      onClick={() => copyToClipboard(enrollment.enroll_command, 'enroll')}
                      className="absolute top-2 right-2 text-muted-foreground hover:text-foreground"
                    >
                      {copiedField === 'enroll' ? (
                        <Check className="w-4 h-4 text-green-500" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>

                <div className="flex justify-between">
                  <Button variant="outline" onClick={() => setFlowStep('install')}>
                    Back
                  </Button>
                  <Button onClick={() => setFlowStep('connect')}>
                    Next: Connect
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3: Connect */}
            {flowStep === 'connect' && (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Start the agent to connect it to the platform.
                </p>

                <div>
                  <Label className="text-xs text-muted-foreground mb-1">Connect Command</Label>
                  <div className="relative">
                    <pre className="bg-muted/50 rounded-lg p-3 pr-10 text-xs font-mono overflow-x-auto">
                      atlas-agent connect
                    </pre>
                    <button
                      onClick={() => copyToClipboard('atlas-agent connect', 'connect')}
                      className="absolute top-2 right-2 text-muted-foreground hover:text-foreground"
                    >
                      {copiedField === 'connect' ? (
                        <Check className="w-4 h-4 text-green-500" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Connection status */}
                <div className="rounded-lg border p-3">
                  {enrollmentCompleted ? (
                    <div className="flex items-center gap-2 text-green-600 text-sm font-medium">
                      <Check className="w-4 h-4" />
                      {t('admin.runners.enrollment.completed')} — Runner is online
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-muted-foreground text-sm">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {t('admin.runners.enrollment.waitingForEnrollment')}
                    </div>
                  )}
                </div>

                <div className="flex justify-between">
                  <Button variant="outline" onClick={() => setFlowStep('enroll')}>
                    Back
                  </Button>
                  <div className="flex gap-2">
                    {enrollmentCompleted && (
                      <Button variant="outline" onClick={() => { onClose(); navigate(`/integrations/runners/${enrollment.agent_id}`); }}>
                        {t('admin.runners.detail.title')}
                      </Button>
                    )}
                    <Button onClick={onClose}>
                      {t('common.close')}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </DialogContent>

      {/* ADR-114 enmienda 1: confirmación de descarte */}
      <DiscardChangesDialog
        open={confirmDiscardOpen}
        onDiscard={() => {
          setConfirmDiscardOpen(false);
          onClose();
        }}
        onCancel={() => setConfirmDiscardOpen(false)}
        title={discardLabels.title}
        message={discardLabels.message}
        confirmLabel={discardLabels.confirm}
        cancelLabel={discardLabels.cancel}
      />
    </Dialog>
  );
}
